import JSZip from 'jszip';
import { DataStorageJSONImageStudio } from "../types/ai/api";
import { convertFileToPngFile, fileToDataUrl, loadImage } from "./images/imageConvertFormats";
import ImageProps, { LayerProps } from "../types/imageStudio/images";
import { stripKnownImageStudioExtensions } from './aiModels/apiImageStudio';
import { fetchWithAuth } from './fetchWithAuth';

 // Funktion zur Umwandlung einer Daten-URL in einen Blob
export const dataURLtoBlob = (dataURL: string) => {
    const arr = dataURL.split(','); // Teile die Daten-URL
    const mime = arr[0].match(/:(.*?);/)?.[1]; // Hole den MIME-Typ
    const bstr = atob(arr[1]); // Entschlüssle den Base64-String
    const n = bstr.length;
    const u8arr = new Uint8Array(n);

    for (let i = 0; i < n; i++) {
      u8arr[i] = bstr.charCodeAt(i); // Erstelle ein Byte-Array
    }

    return new Blob([u8arr], { type: mime }); // Erstelle und gebe einen Blob zurück
  };

export const base64ToFile = (base64String: string, filename: string): File => {
    // Verwende den gesamten `base64String`, da kein Präfix vorhanden ist
    const byteCharacters = atob(base64String);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    };

    const byteArray = new Uint8Array(byteNumbers);
    // Blob aus den Binärdaten erstellen
    const blob = new Blob([byteArray], { type: 'image/png' });
    // File-Objekt erstellen
    const file = new File([blob], filename, { type: 'image/png' });

    return file;
  };

export const fileToBase64 = (file:any): Promise<string | null> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        
        reader.onload = () => {
          if (!reader.result) {
            resolve(null);
          } else {
            // Entfernt den "data:image/png;base64," Teil
            const base64 = reader.result.toString().split(",")[1];
            resolve(base64);
          }
        }; // nur Base64 ohne prefix
        reader.onerror = error => reject(error);
        reader.readAsDataURL(file);
    });
}

export const downloadJSON = (jsonObj: object, configurationName:string) => {
    const jsonStr = JSON.stringify(jsonObj);
    const blob = new Blob([jsonStr],{type:"application/json"});

    const url = URL.createObjectURL(blob);
    //virtuellen Anchor erstellen
    const link = document.createElement('a');
    link.href = url;
    // eslint-disable-next-line no-restricted-syntax -- JSON-Konfigurations-Export, kein Media-Download, Art. 50 KI-VO nicht einschlaegig
    link.download = `${configurationName}.json`;
    //introduce download by click on element
    document.body.appendChild(link);
    link.click();

    //remove element ans url after downloading
    if(document.body.contains(link)){
      document.body.removeChild(link);
    }
    URL.revokeObjectURL(url);
  };

// fetchWithAuth, not plain fetch: `url` is frequently a cProxy result URL behind Bearer-auth
// (e.g. /api/cproxy/v1/downloads/*). Harmless for the other cases this is called with
// (data:/blob: URIs ignore the extra header entirely) - see TASKS.md, "cProxy downloads require
// auth". A plain fetch() here silently succeeds with the 401 error body as "image bytes" instead
// of failing loudly, corrupting the resulting File.
export const urlToFile = async(url: string, filename: string): Promise<File> =>{
  const response = await fetchWithAuth(url);
  const blob = await response.blob();
  return new File([blob], filename, { type: blob.type });
};

export const fileToCanvas = async (file: File): Promise<HTMLCanvasElement> => {
    const dataUrl = await fileToDataUrl(file);
    const img = await loadImage(dataUrl);

    const canvas = document.createElement("canvas");
    canvas.width = img.naturalWidth || img.width;
    canvas.height = img.naturalHeight || img.height;

    const ctx = canvas.getContext("2d");
    if (!ctx) {
        throw new Error("Could not create canvas context.");
    }

    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

    return canvas;
};

export const zipImageStudioProjectFile = async (base: ImageProps, layer:LayerProps[], createdAt:Date, baseName:string): Promise<{zip:FormData,path:string} | null> => {
  let description: DataStorageJSONImageStudio = {
    layer: layer.map(item => {
      return {
        type: item.type,
        layerID: item.layerID,
        position: item.position,
        width: item.width,
        height: item.height,
        orderNumber: item.orderNumber,
        rotation: item.rotation,
        hidden: item.hidden,
        flipHorizontal: item.flipHorizontal,
        flipVertical: item.flipVertical,
        imageLayerStyle: item.imageLayerStyle,
        textData: item.textData,
        drawingData: item.drawingData
      }
    }),
    lastUse: new Date(),
    createdAt: createdAt
  };

  //includes layer info data (positioning, rotation,...)
  const descriptionJson = JSON.stringify(description);
  const zip = new JSZip();
  zip.file('description.json',descriptionJson);

  //create images for layer and base: sort in subfolder for redirecting
  const baseDir = zip.folder('base');
  const layerDir = zip.folder('layer');

  //add base image
  if (baseDir && base.file) {
    baseDir.file(`${stripKnownImageStudioExtensions(baseName)}.png`, base.file);
  }

  //add layer images: name <layerID>.png 
  for(let singleLayer of layer){
    if(singleLayer.type !== 'text' && layerDir){
      const layerFilename = `${singleLayer.layerID}.png`
      const img = singleLayer.imageData && singleLayer.imageData.file
                    ? await convertFileToPngFile(singleLayer.imageData.file,layerFilename)
                      : singleLayer.drawingData && singleLayer.drawingData.rasterSrc
                        ? await urlToFile(singleLayer.drawingData.rasterSrc,layerFilename)
                        : null
      if(!img) continue;
      layerDir.file(layerFilename,img);
    }
  }
  try {
    const content = await zip.generateAsync({ type: "blob" });

    const zipFormData = new FormData();
    const filename = `${stripKnownImageStudioExtensions(baseName)}.sngImg`;

    zipFormData.append("file", content, filename);

    return {
      path: filename,
      zip: zipFormData,
    };
  } catch {
    return null;
  }
};

export const unzipImageStudioProjectFile = async (file: File) => {
  const zip = await JSZip.loadAsync(file);

  const descriptionFile = zip.file("description.json");

  if (!descriptionFile) {
    throw new Error("description.json missing");
  }

  const descriptionText = await descriptionFile.async("string");
  const description = JSON.parse(descriptionText) as DataStorageJSONImageStudio;

  let baseFile: File| null = null;
  const layerFiles: File[] = [];

  for (const [path, zipEntry] of Object.entries(zip.files)) {
    if (zipEntry.dir) continue;

    if (path.startsWith("base/")) {
      const blob = await zipEntry.async("blob");
      const filename = path.split("/").pop() ?? "base.png";
      baseFile = new File([blob], filename, { type: blob.type || "image/png" });
    }

    if (path.startsWith("layer/")) {
      const blob = await zipEntry.async("blob");
      const filename = path.split("/").pop() ?? "layer.png";

      layerFiles.push(
        new File([blob], filename, { type: blob.type || "image/png" })
      );
    }
  }

  return {
    description,
    baseFile,
    layerFiles,
  };
};